<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="relative z-50" @close="$emit('close')">
            <TransitionChild as="template" enter="ease-out duration-200" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-150" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-gray-950/90" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 flex items-center justify-center p-4 sm:p-8">
                <DialogPanel class="relative flex h-full w-full max-w-6xl flex-col" @keydown.left.prevent="previous" @keydown.right.prevent="next">
                    <div class="flex items-center justify-between pb-3 text-white">
                        <DialogTitle as="p" class="text-sm font-medium">
                            Photo {{ index + 1 }} of {{ photos.length }}
                        </DialogTitle>
                        <button
                            type="button"
                            class="rounded-full p-2 text-white/80 hover:bg-white/10 hover:text-white"
                            aria-label="Close photos"
                            @click="$emit('close')"
                        >
                            <font-awesome-icon :icon="faXmark" class="h-6 w-6" aria-hidden="true" />
                        </button>
                    </div>

                    <div class="relative flex min-h-0 flex-1 items-center justify-center">
                        <img
                            :src="photos[index]"
                            :alt="`${alt} — photo ${index + 1}`"
                            class="max-h-full max-w-full rounded object-contain"
                            data-test="lightbox-photo"
                        />
                        <template v-if="photos.length > 1">
                            <button
                                type="button"
                                class="absolute left-0 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-3 text-gray-900 shadow hover:bg-white sm:left-2"
                                aria-label="Previous photo"
                                @click="previous"
                            >
                                <font-awesome-icon :icon="faChevronLeft" class="h-5 w-5" aria-hidden="true" />
                            </button>
                            <button
                                type="button"
                                class="absolute right-0 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-3 text-gray-900 shadow hover:bg-white sm:right-2"
                                aria-label="Next photo"
                                @click="next"
                            >
                                <font-awesome-icon :icon="faChevronRight" class="h-5 w-5" aria-hidden="true" />
                            </button>
                        </template>
                    </div>

                    <div v-if="photos.length > 1" class="mt-3 flex gap-2 overflow-x-auto pb-1">
                        <button
                            v-for="(url, thumbnailIndex) in photos"
                            :key="`${url}-${thumbnailIndex}`"
                            type="button"
                            :class="[
                                'h-14 w-20 shrink-0 overflow-hidden rounded ring-2',
                                thumbnailIndex === index ? 'ring-white' : 'ring-transparent opacity-60 hover:opacity-100',
                            ]"
                            :aria-label="`Show photo ${thumbnailIndex + 1}`"
                            @click="index = thumbnailIndex"
                        >
                            <img :src="url" alt="" class="h-full w-full object-cover" />
                        </button>
                    </div>
                </DialogPanel>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
import { ref, watch } from 'vue'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faChevronLeft, faChevronRight, faXmark } from '@fortawesome/pro-solid-svg-icons'

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    photos: {
        type: Array,
        required: true,
    },
    startIndex: {
        type: Number,
        default: 0,
    },
    alt: {
        type: String,
        default: 'Property',
    },
})

defineEmits(['close'])

const index = ref(props.startIndex)

watch(
    () => [props.show, props.startIndex],
    ([show, startIndex]) => {
        if (show) {
            index.value = Math.min(Math.max(startIndex, 0), Math.max(props.photos.length - 1, 0))
        }
    },
)

const previous = () => {
    index.value = (index.value - 1 + props.photos.length) % props.photos.length
}

const next = () => {
    index.value = (index.value + 1) % props.photos.length
}
</script>
